import { useMutation } from '@apollo/client';
import { useSnackbar } from 'notistack';
import React, { useState } from 'react';
import ShareButton from 'screens/company-user/job/job-v1/title-row/ShareButton';

import EditIcon from '@mui/icons-material/Edit';
import { Box, Paper, Typography, Button, Grid } from '@mui/material';
import { makeStyles } from '@mui/styles';

import { Job } from '@libs/graphql-types';

import { UPDATE_JOB } from '../../../../job-form/queries';
import { JobEditPopup } from './JobEditPopup';
import { PerformanceMetrics } from './PerformanceMetrics';

const useStyles = makeStyles((theme: any) => ({
  root: {
    marginBottom: theme.spacing(3),
    backgroundColor: 'transparent',
    boxShadow: 'none',
  },
  header: {
    padding: theme.spacing(0),
  },
  titleContainer: {
    marginBottom: theme.spacing(6),
  },
  title: {
    fontSize: '1.5rem',
    lineHeight: '1.75rem',
    fontWeight: 500,
    color: theme.palette.text.primary,

    [theme.breakpoints.down('md')]: {
      marginBottom: theme.spacing(4),
    },
  },
  actionsContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(1),

    [theme.breakpoints.down('sm')]: {
      justifyContent: 'center',
      gap: theme.spacing(2),
      marginTop: theme.spacing(2),
    },
  },
  actionButton: {
    textTransform: 'uppercase',
    minWidth: 'unset',
    padding: theme.spacing(1, 2),
    [theme.breakpoints.down('sm')]: {
      flex: '1 1 50%',
      fontSize: '0.875rem',
    },
    '& .MuiButton-startIcon': {
      margin: 0,
      marginRight: theme.spacing(1),
      [theme.breakpoints.down('sm')]: {
        marginRight: theme.spacing(0.5),
      },
    },
  },
  actionIcon: {
    fontSize: '1.25rem',
    [theme.breakpoints.down('sm')]: {
      fontSize: '1rem',
    },
  },
  statusChip: {
    marginLeft: theme.spacing(2),
  },
  shareButton: {
    textTransform: 'uppercase',
    [theme.breakpoints.down('sm')]: {
      flex: '1 1 50%',
      fontSize: '0.875rem',
      '& .MuiButton-endIcon': {
        marginLeft: theme.spacing(0.5),
      },
    },
  },
}));

interface JobHeaderProps {
  jobData?: Job;
  refetch?: () => void;
}

export const JobHeader: React.FC<JobHeaderProps> = ({ jobData, refetch }) => {
  const classes = useStyles();
  const [editOpen, setEditOpen] = useState(false);
  const [updateJob] = useMutation(UPDATE_JOB);
  const { enqueueSnackbar } = useSnackbar();

  if (!jobData) return null;

  const handleEdit = async (values: {
    description: string;
    campaign_owner_id: string;
  }) => {
    await updateJob({
      variables: {
        id: jobData.id,
        description: values.description,
        campaign_owner_id: values.campaign_owner_id,
        name: jobData.name,
      },
    });
    if (refetch) await refetch();

    enqueueSnackbar('Job updated successfully', {
      variant: 'success',
    });
  };

  return (
    <Paper className={classes.root}>
      <Box className={classes.header}>
        <Grid container alignItems="center" className={classes.titleContainer}>
          <Grid item flexGrow={1}>
            <Typography className={classes.title}>
              {jobData.name || (jobData as any).title || ''}
            </Typography>
          </Grid>

          <Grid item xs={12} md="auto" container spacing={4}>
            <Grid item xs={6}>
              <Button
                variant="outlined"
                color="info"
                fullWidth
                className={classes.actionButton}
                onClick={() => setEditOpen(true)}
                startIcon={<EditIcon className={classes.actionIcon} />}
              >
                Edit
              </Button>
            </Grid>
            <Grid item xs={6}>
              <ShareButton
                job={jobData as Job}
                className={classes.shareButton}
              />
            </Grid>
          </Grid>
        </Grid>

        <PerformanceMetrics jobData={jobData} />
      </Box>
      <JobEditPopup
        open={editOpen}
        onClose={() => setEditOpen(false)}
        jobData={jobData}
        onSubmit={handleEdit}
      />
    </Paper>
  );
};
